import type { CaseNotice, Company, DomainNotice } from "./types";

export const mockCases: CaseNotice[] = [
  {
    caseNumber: "NCVIC-2024-001234",
    reason:
      "This content was removed following a lawful request citing violations of the following:",
    legalViolations: [
      "18 U.S.C. § 1591 (Sex Trafficking by Force, Fraud, or Coercion)",
      "18 U.S.C. § 2252A (Child Sexual Abuse Material)",
    ],
    termsViolations: [
      "Cloudflare Terms of Service",
      "Google Terms of Service",
      "Amazon Web Services Acceptable Use Policy",
    ],
    dateReported: "November 15, 2024",
  },
  {
    caseNumber: "NCVIC-2024-005678",
    reason:
      "This content was removed following a lawful request citing violations of the following:",
    legalViolations: [
      "18 U.S.C. § 2252 (Material Involving the Sexual Exploitation of Minors)",
      "18 U.S.C. § 2256 (Definitions)",
    ],
    termsViolations: [
      "Microsoft Azure Terms of Service",
      "Cloudflare Terms of Service",
    ],
    dateReported: "October 3, 2024",
  },
];

export const mockDomains: DomainNotice[] = [
  {
    domain: "illegalcontent.com",
    status: "Seized",
    reason:
      "This domain was seized following a lawful request citing violations of the following:",
    legalViolations: [
      "18 U.S.C. § 1591 (Sex Trafficking by Force, Fraud, or Coercion)",
      "18 U.S.C. § 2252A (Child Sexual Abuse Material)",
      "18 U.S.C. § 1029 (Fraud and Related Activity in Connection with Access Devices)",
    ],
    termsViolations: [
      "Cloudflare Terms of Service",
      "Google Terms of Service",
      "Domain Registrar Acceptable Use Policy",
    ],
    provider: "Federal Bureau of Investigation (FBI)",
    actionDate: "November 20, 2024",
    caseNumber: "NCVIC-2024-001234",
  },
  {
    domain: "badactor.net",
    status: "Suspended",
    reason:
      "This domain was suspended by the hosting provider following a lawful request citing violations of the following:",
    legalViolations: [
      "18 U.S.C. § 2252 (Material Involving the Sexual Exploitation of Minors)",
    ],
    termsViolations: [
      "Hosting Provider Terms of Service",
      "Domain Registrar Acceptable Use Policy",
    ],
    provider: "Amazon Web Services",
    actionDate: "December 1, 2024",
    caseNumber: "NCVIC-2024-005679",
  },
];

export const mockCompanies: Company[] = [
  {
    name: "Cloudflare",
    policy:
      "Cloudflare is committed to maintaining a safe and lawful internet. We respond to valid legal requests and enforce our Terms of Service to prevent abuse of our platform.",
    enforcement:
      "Cloudflare reviews all takedown requests in accordance with applicable laws and our Terms of Service. We work closely with law enforcement agencies and NCMEC to combat illegal content, particularly content involving child exploitation.",
    blockedDomains: [
      {
        domain: "illegalcontent.com",
        caseNumber: "NCVIC-2024-001234",
        status: "Blocked",
        dateBlocked: "November 20, 2024",
        reason: "Domain hosting child sexual abuse material (CSAM)",
        rationale:
          "Following a lawful request from law enforcement, Cloudflare terminated services to this domain for hosting illegal content that violates 18 U.S.C. § 2252A. The decision was made in accordance with our Terms of Service and legal obligations.",
        legalViolations: [
          "18 U.S.C. § 2252A (Child Sexual Abuse Material)",
          "18 U.S.C. § 1591 (Sex Trafficking by Force, Fraud, or Coercion)",
        ],
        appeals: [
          {
            date: "November 25, 2024",
            decision: "Denied",
            explanation:
              "After thorough review, Cloudflare determined that the evidence presented in the appeal does not contradict the original findings. The domain was verified to be hosting CSAM, and the decision to terminate services remains in effect.",
          },
          {
            date: "November 28, 2024",
            decision: "Denied",
            explanation:
              "Second appeal reviewed. New evidence submitted was insufficient to overturn the original decision. Law enforcement confirmation and digital forensic evidence conclusively demonstrate violations of federal law.",
          },
        ],
      },
      {
        domain: "csam-site.net",
        caseNumber: "NCVIC-2024-003421",
        status: "Blocked",
        dateBlocked: "October 8, 2024",
        reason: "Distribution of prohibited content",
        rationale:
          "Cloudflare received a verified report through the NCMEC CyberTipline regarding CSAM distribution. After investigation, we immediately terminated all services and reported the domain to appropriate authorities.",
        legalViolations: ["18 U.S.C. § 2252A (Child Sexual Abuse Material)"],
        appeals: [
          {
            date: "October 15, 2024",
            decision: "Denied",
            explanation:
              "Appeal was reviewed in accordance with our policies. The NCMEC CyberTipline report was verified and corroborated by our Trust & Safety team. The block remains in place.",
          },
        ],
      },
      {
        domain: "exploit-network.com",
        caseNumber: "NCVIC-2024-002890",
        status: "Blocked",
        dateBlocked: "September 15, 2024",
        reason: "Child exploitation and trafficking",
        rationale:
          "Domain was identified as part of a broader investigation into an organized child exploitation network. Cloudflare cooperated with federal law enforcement and terminated services immediately upon receiving the legal request.",
        legalViolations: [
          "18 U.S.C. § 1591 (Sex Trafficking by Force, Fraud, or Coercion)",
          "18 U.S.C. § 2252 (Material Involving the Sexual Exploitation of Minors)",
        ],
        appeals: [],
      },
    ],
  },
  {
    name: "Amazon Web Services",
    policy:
      "AWS maintains a zero-tolerance policy for illegal content. We comply with all lawful requests and enforce our Acceptable Use Policy to ensure our services are not used for illegal activities.",
    enforcement:
      "AWS works proactively with law enforcement agencies and organizations like NCMEC to identify and remove illegal content from our platform. We maintain strict policies against child exploitation and take immediate action when violations are identified.",
    blockedDomains: [
      {
        domain: "badactor.net",
        caseNumber: "NCVIC-2024-005679",
        status: "Suspended",
        dateBlocked: "December 1, 2024",
        reason: "Hosting child sexual abuse material",
        rationale:
          "AWS suspended this account following verification of CSAM content. The decision was made in accordance with our Acceptable Use Policy and federal law. All data was preserved for law enforcement investigation.",
        legalViolations: [
          "18 U.S.C. § 2252 (Material Involving the Sexual Exploitation of Minors)",
        ],
        appeals: [],
      },
      {
        domain: "illegal-marketplace.com",
        caseNumber: "NCVIC-2024-004567",
        status: "Terminated",
        dateBlocked: "November 12, 2024",
        reason: "Operating illegal marketplace for exploitative content",
        rationale:
          "Following a multi-agency investigation, AWS determined this domain was operating a marketplace for illegal content including CSAM. All AWS services were immediately terminated and evidence was provided to law enforcement.",
        legalViolations: [
          "18 U.S.C. § 2252A (Child Sexual Abuse Material)",
          "18 U.S.C. § 1591 (Sex Trafficking by Force, Fraud, or Coercion)",
        ],
        appeals: [
          {
            date: "November 18, 2024",
            decision: "Denied",
            explanation:
              "AWS reviewed the appeal and confirmed that the account was involved in distributing illegal content. The decision to terminate services is final and supported by evidence from federal law enforcement.",
          },
        ],
      },
    ],
  },
  {
    name: "Google",
    policy:
      "Google has a strict policy prohibiting illegal content on our platforms. We work closely with law enforcement and safety organizations to protect children and combat exploitation.",
    enforcement:
      "Google employs advanced detection technologies and human review teams to identify and remove illegal content. We report all instances of child exploitation to NCMEC and cooperate fully with law enforcement investigations.",
    blockedDomains: [
      {
        domain: "harmful-content.org",
        caseNumber: "NCVIC-2024-006234",
        status: "Removed",
        dateBlocked: "November 28, 2024",
        reason: "Distribution of child sexual abuse material",
        rationale:
          "Google Cloud Platform detected and removed this domain for violating our Terms of Service and federal law. Our automated systems flagged the content, which was confirmed by our Trust & Safety team before immediate removal.",
        legalViolations: ["18 U.S.C. § 2252A (Child Sexual Abuse Material)"],
        appeals: [],
      },
    ],
  },
];
